import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../../../contexts/SupabaseContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Square, CheckSquare } from 'lucide-react';
import type { Task } from '../../../types/database';

const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { tasks, loadTasks, createTask, updateTask, projects, loadProjects, updateProject } = useSupabase();

  // Load all tasks on mount to ensure we have the data
  useEffect(() => {
    loadProjects();
    loadTasks();
  }, [loadProjects, loadTasks]);

  const project = projects.find(p=>p.id===projectId);
  const projectTasks = tasks.filter(t=>t.project_id === projectId);

  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');

  const totalSubtasks = projectTasks.length;
  const completedSubtasks = projectTasks.filter(st => st.status === 'completed').length;
  const percentComplete = totalSubtasks > 0 ? Math.round((completedSubtasks / totalSubtasks) * 100) : 0;

  const handleAddSubtask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newSubtaskTitle.trim() || !project) return;
    await createTask({
      title: newSubtaskTitle.trim(),
      category: 'task',
      status: 'todo',
      project_id: project.id,
    } as any); // casting because due_date etc not required
    setNewSubtaskTitle('');
  };

  const toggleSubtaskStatus = async (subtask: Task) => {
    const newStatus = subtask.status === 'completed' ? 'todo' : 'completed';
    await updateTask(subtask.id, { status: newStatus });
  };

  // Toggle whole project complete
  const toggleProjectStatus = async () => {
    if (!project) return;
    const newStatus = project.status === 'completed' ? 'todo' : 'completed';
    await updateProject(project.id, { status: newStatus });
  };

  if (!project) {
    return <div>Loading...</div>;
  }

  return (
    <section className="project-details">
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        <button className="button" onClick={() => navigate('/projects')}>&larr; Back to Projects</button>
        <button className="button" onClick={() => updateTask(project.id, { category: 'task' }).then(()=>navigate('/tasks'))}>Demote to Task</button>
      </div>

      {/* Project header styled like task item */}
      <div className="project-header-item">
        <button className={`checkbox-btn ${project.status === 'completed' ? 'completed' : ''}`} onClick={toggleProjectStatus}>
          {project.status === 'completed' ? '✓' : ''}
        </button>
        <div className="project-main-content">
          <h2>{project.title}</h2>
          {project.description && <p className="project-description">{project.description}</p>}
          {project.due_date && <p className="project-due"><strong>Due:</strong> {new Date(project.due_date).toLocaleDateString()}</p>}
          <p className="project-progress"><strong>Progress:</strong> {percentComplete}% ({completedSubtasks}/{totalSubtasks})</p>
        </div>
      </div>

      <h3 className="mt-6 mb-2 font-medium">Tasks</h3>
      <form onSubmit={handleAddSubtask} className="flex items-center gap-2 mb-4">
        <Input
          placeholder="Add a task..."
          value={newSubtaskTitle}
          onChange={e=>setNewSubtaskTitle(e.target.value)}
        />
        <Button size="sm" type="submit" disabled={!newSubtaskTitle.trim()}>Add</Button>
      </form>

      <ul className="space-y-1">
        {projectTasks.map(task => (
          <li key={task.id} className="flex items-center gap-2 text-sm">
            <button onClick={()=>toggleSubtaskStatus(task)} className="p-0 w-5 h-5">
              {task.status==='completed'? <CheckSquare className="w-4 h-4 text-blue-600"/> : <Square className="w-4 h-4 text-muted-foreground"/>}
            </button>
            <span className={task.status==='completed'? 'line-through text-muted-foreground':''}>{task.title}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ProjectDetails; 